"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { ProjectDetail } from "@/lib/types";

type ProjectOpenContextValue = {
  openSlugs: string[];
  detailCache: Record<string, ProjectDetail>;
  openProject: (slug: string) => void;
  closeAll: () => void;
  seedProject: (project: ProjectDetail) => void;
  getDetail: (slug: string) => ProjectDetail | null;
};

const ProjectOpenContext = createContext<ProjectOpenContextValue | null>(null);

/**
 * Lives in the root layout so open rows survive Next.js soft-nav
 * (Next patches history.pushState and may re-render the page slot).
 */
export function ProjectOpenProvider({ children }: { children: ReactNode }) {
  const [openSlugs, setOpenSlugs] = useState<string[]>([]);
  const [detailCache, setDetailCache] = useState<Record<string, ProjectDetail>>({});
  const cacheRef = useRef(detailCache);
  cacheRef.current = detailCache;

  const fetchDetail = useCallback(async (slug: string) => {
    if (cacheRef.current[slug]) return cacheRef.current[slug];
    try {
      const res = await fetch(`/api/projects/${encodeURIComponent(slug)}`);
      if (!res.ok) return null;
      const data = (await res.json()) as ProjectDetail;
      setDetailCache((prev) => ({ ...prev, [slug]: data }));
      return data;
    } catch {
      return null;
    }
  }, []);

  const openProject = useCallback(
    (slug: string) => {
      setOpenSlugs((prev) => (prev.includes(slug) ? prev : [...prev, slug]));
      window.history.pushState({ projectSlug: slug }, "", `/projects/${slug}`);
      void fetchDetail(slug);
    },
    [fetchDetail],
  );

  const closeAll = useCallback(() => setOpenSlugs([]), []);

  const seedProject = useCallback((project: ProjectDetail) => {
    setDetailCache((prev) =>
      prev[project.slug] ? prev : { ...prev, [project.slug]: project },
    );
    setOpenSlugs((prev) =>
      prev.includes(project.slug) ? prev : [...prev, project.slug],
    );
  }, []);

  const getDetail = useCallback(
    (slug: string) => detailCache[slug] ?? null,
    [detailCache],
  );

  const value = useMemo(
    () => ({ openSlugs, detailCache, openProject, closeAll, seedProject, getDetail }),
    [openSlugs, detailCache, openProject, closeAll, seedProject, getDetail],
  );

  return (
    <ProjectOpenContext.Provider value={value}>{children}</ProjectOpenContext.Provider>
  );
}

export function useProjectOpen() {
  const ctx = useContext(ProjectOpenContext);
  if (!ctx) throw new Error("useProjectOpen must be used within ProjectOpenProvider");
  return ctx;
}
